<template>
    <div class="dashboard-graphs-container">
        <div class="w-100">
            <div class="row mx-0 my-3">
                <!-- Fuel type -->
                <div 
                    class="col-md-6 col-lg-3"
                    :class="{'mb-2 order-1' : $breakpoint.mdAndDown, 'pr-0': !$breakpoint.mdAndDown}"
                >
                    <div class="position-relative">
                        <el-select  v-model="filters.fuels"
                                    filterable
                                    clearable
                                    multiple
                                    collapse-tags
                                    no-match-text="No matching fuel type"
                                    value-key="_id"
                                    placeholder="Search fuel here..."
                                    class="float-right mr-1"
                                    style="width: 100%">
                            <el-option
                                v-for="item in fuelsList"
                                :key="item._id"
                                :label="item.name"
                                :value="item">
                            </el-option>
                        </el-select>
                    </div>
                </div>

                <!-- Terminals list -->
                <div 
                    class="col-md-6 col-lg-3"
                    :class="{'mb-2 order-1' : $breakpoint.mdAndDown, 'pr-0': !$breakpoint.smAndDown}"
                >
                    <div class="position-relative">
                        <el-select  v-model="filters.terminals"
                                    filterable
                                    clearable
                                    multiple
                                    collapse-tags
                                    no-match-text="No matching terminal"
                                    value-key="_id"
                                    placeholder="Search terminal here..."
                                    class="float-right mr-1"
                                    style="width: 100%"
                                    :disabled="filters.fuels.find(fuel => fuel.type == 'BUNKERING') && !filters.fuels.find(fuel => fuel.type == 'SPOT')">
                            <el-option
                                v-for="item in getTerminalChanges"
                                :key="item._id"
                                :label="item.name"
                                :value="item">
                            </el-option>
                        </el-select>
                    </div>
                </div>

                <!-- Counterpart list -->
                <div 
                    class="col-md-6 col-lg-3"
                    :class="{'mb-2 order-1 ' : $breakpoint.mdAndDown, 'pr-0': !$breakpoint.mdAndDown}"
                >
                    <div class="position-relative">
                        <el-select  v-model="filters.counterparts"
                                    filterable
                                    clearable
                                    multiple
                                    collapse-tags
                                    :no-match-text="'No matching ' + (userLoggedIn.category === 'buyer' ? 'seller' : 'buyer')"
                                    value-key="id"
                                    :placeholder="'Search ' + (userLoggedIn.category == 'buyer' ? 'seller' : 'buyer') + ' here...'" 
                                    class="float-right mr-1"
                                    style="width: 100%">
                            <el-option
                                v-for="item in counterpartList"
                                :key="item.id"
                                :label="item.name"
                                :value="item">
                            </el-option>
                        </el-select>
                    </div>
                </div>

                <!-- Date range selector -->
                <div 
                    class="col-md-6 col-lg-3"
                    :class="{'order-0 mb-2' : $breakpoint.mdAndDown, 'pr-0': !$breakpoint.smAndDown,  'pr-3': !$breakpoint.mdAndDown}"
                >
                    <vc-date-picker 
                        mode="range" 
                        v-model="filters.dateRange" 
                        :masks="{ input: 'DD/MMM/YY' }" 
                        class="vc-date-picker"
                        :input-props='{ placeholder: filters.dateRange , readonly: true }'
                    >
                    </vc-date-picker>
                </div>
            </div>

            <apexchart type="bar" width="100%" height="340px" v-if="fuelsList.length > 0 && graphColorsLoaded"
                :options="chartOptions" :series="series" ref="fuelChart">
            </apexchart>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import breakpoint from '@/mixins/index';
import { getOrderStatistics } from '@/api/orders';
const numeral = require("numeral");
const moment = require('moment');
const platform = process.env.VUE_APP_Platform ?? "DTN";
const graphColors = require(`@/assets/scss/platformScss/${platform}/graphs.scss`);

export default {
    mixins: [breakpoint],
    data(){
        return {
            graphColors,
            filters: {
                fuels: [],
                terminals: [],
                counterparts: [],
                dateRange: {
                    start: new Date(moment().subtract(30, 'days')),
                    end: new Date()
                },
            },
            orderStatistics: {},
        }
    },
    created: async function() {
        if (this.userLoggedIn.category == 'seller' && this.customerList.length === 0) {
            await this.getRelationships();
        }

        await this.setOrderStatistics();
    },
    computed: {
        ...mapState('accounts', ['suppliers', 'customerList']),
        ...mapGetters('fuelsType', ['getFuelsType']),
        ...mapState('login', ['userLoggedIn']),
        ...mapGetters('terminals', ['getTerminalChanges']),
        ...mapGetters('metadata', ['productConfig', 'networkConfig']),
        currentFuels: {
          get: function() {
            return this.filters.fuels.length > 0 ? this.filters.fuels : this.fuelsList
          }
        },
        fuelsList: {
            get: function(){
                let fuels = [];

                if (this.getFuelsType.length > 0){
                    fuels = this.getFuelsType.filter(fuel => fuel.type !== 'NOT4SALE');
                }

                return fuels;
            }
        },
        counterpartList: {
            get: function(){
                let counterparts = []

                if ((this.userLoggedIn.category == 'buyer' && this.suppliers.length > 0) ||
                    (this.userLoggedIn.category == 'seller' && this.customerList.length > 0)){
                    counterparts = this.userLoggedIn.category == 'buyer' ? this.suppliers : this.customerList
                }

                return counterparts;
            }
        },
        series: {
            get: function(){
              return this.getSeriesData(this.orderStatistics);
            }
        },
        graphColorsLoaded() {
            return graphColors != null
        },
        chartOptions: function(){
            if (this.fuelsList.length > 0){
                return {
                    plotOptions: {
                        bar: {
                            distributed: true
                        }
                    },
                    dataLabels: {
                        enabled: false,
                    },
                    fill: {
                        colors: [this.graphColors.VolumePerProductGraphColor1, this.graphColors.VolumePerProductGraphColor2, this.graphColors.VolumePerProductGraphColor3, this.graphColors.VolumePerProductGraphColor4, this.graphColors.VolumePerProductGraphColor5]
                    },
                    noData: {
                        text: "No Data for that criteria",
                        align: "center",
                        verticalAlign: "middle"
                    },
                    chart: {
                        id: 'vuechart-example',
                    },
                    xaxis: {
                        categories: (this.currentFuels).map(fuel => fuel.name)
                    },
                    yaxis: {
                        labels: {
                            formatter: function(value) {
                                return numeral(value).format("0,0");
                            }
                        }
                    },
                    animations: {
                        enabled: true,
                        easing: 'easeinout',
                        speed: 2000,
                        animateGradually: {
                            enabled: true,
                            delay: 100,
                        },
                        dynamicAnimation: {
                            enabled: true,
                            speed: 100,
                        }
                    }
                }
            }
        },
        removeTerminalsOnBunkering(){
            if (this.filters.fuels){
                if (this.filters.fuels.find(fuel => fuel.type == 'BUNKERING') && !this.filters.fuels.find(fuel => fuel.type == 'SPOT')) {
                    return this.filters.terminals = []
                }
            }
        }
    },
    watch: {
        chartOptions(){},
        removeTerminalsOnBunkering(){},
        "filters.fuels"(){
          this.setOrderStatistics()
        },
        "filters.dateRange.start"(){
          this.setOrderStatistics()
        },
        "filters.dateRange.end"(){
          this.setOrderStatistics()
        },
        "filters.terminals"(){
          this.setOrderStatistics()
        },
        "filters.counterparts"(){
          this.setOrderStatistics()
        },
    },
    methods: {
        ...mapActions('accounts', ['getRelationships']),
        async setOrderStatistics(){
          const payload = {
            fuels: this.currentFuels.map(({_id}) => _id),
            startDate: `${moment(this.filters.dateRange.start).format("YYYY-MM-DD")}T01:00:00.000000Z`,
            endDate: `${moment(this.filters.dateRange.end).format("YYYY-MM-DD")}T23:00:00.000000Z`,
            terminals: this.filters.terminals.map(({_id}) => _id),
            counterparts: this.filters.counterparts.map(({id}) => id),
          }
          const { statistics } = await getOrderStatistics(payload);
          this.orderStatistics = statistics;
        },
        getSeriesData(statistics){
          const data = this.currentFuels.map(({_id}) => statistics && statistics[_id] ? statistics[_id] :  0 );
          return [{ name: "Total volume", data}];
        },
    }
}
</script>

<style lang="scss">
@import "@/assets/scss/components/_datepicker.scss";
</style>
